import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Breadcrumb, Button, Card, Skeleton, StatusBadge, Timeline, useLocale, useToast, type TimelineEvent } from '@ministry/ui/internal';

import { getAppointment, getAppointmentPolicy } from '../lib/appointments.js';
import { appointmentStatusEntry, availabilityStatusEntry } from '../lib/appointment-lifecycle.js';
import { ApiError } from '../lib/api-client.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { formatInstantDisplay } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { ForbiddenState } from '../components/ForbiddenState.js';
import { ServerErrorState } from '../components/ServerErrorState.js';
import {
  AppointmentCancelModal,
  AppointmentDeclineModal,
  AppointmentScheduleModal,
} from '../components/AppointmentActionModals.js';

/** SCREEN_SPECS.md I15's "Appointment summary card" half — the single-record view reached from I14's row click. Entrance verification itself (the scan/manual-reference tool) is `EntranceVerificationPage`, a separate route: verification is not tied to knowing which appointment you're looking at in advance — that is exactly what it looks up. */
export function AppointmentDetailPage(): React.JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = React.useState<'accept' | 'reschedule' | 'decline' | 'cancel' | null>(null);

  const query = useQuery({
    queryKey: ['appointment', appointmentId],
    queryFn: ({ signal }) => getAppointment(appointmentId as string, signal),
    enabled: Boolean(appointmentId),
  });

  const policyQuery = useQuery({
    queryKey: ['appointment-policy'],
    queryFn: ({ signal }) => getAppointmentPolicy(signal),
    staleTime: 5 * 60_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
  };
  const notifySuccess = (key: string) => push({ variant: 'success', title: t(key) });

  if (!appointmentId) return <></>;

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton shape="block" className="h-8 w-64" />
        <Skeleton shape="list-row" count={4} />
      </div>
    );
  }

  if (query.isError) {
    const code = query.error instanceof ApiError ? query.error.code : undefined;
    // NOT_FOUND and every FORBIDDEN_* variant share ONE property that a generic
    // network/server failure does not: retrying with the SAME request cannot possibly
    // help (API_CONTRACT.md §4.1/I24: "never suggests retrying, since retrying cannot
    // help"), so those two branches render `ForbiddenState`'s own four-way copy (which
    // covers `NOT_FOUND` too) with no retry action at all, while everything else keeps a
    // real retry button.
    if (code === 'NOT_FOUND' || (code !== undefined && code.startsWith('FORBIDDEN'))) {
      return (
        <div className="flex flex-col gap-4">
          <PageHeader title={t('appointments.detail.title')} />
          <ForbiddenState error={query.error} backTo={ROUTES.appointments} backLabel={t('appointments.inbox.title')} />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('appointments.detail.title')} />
        <ServerErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const appointment = query.data;
  if (!appointment) return <></>;

  const canDecideFurther = appointment.status.code === 'pending' || appointment.status.code === 'accepted' || appointment.status.code === 'rescheduled';
  const reasons = policyQuery.data?.declineReasons ?? [];

  const events: TimelineEvent[] = appointment.timeline.map((event) => ({
    id: String(event.seqNo),
    stage: appointmentStatusEntry({ code: event.toStatus }),
    when: formatInstantDisplay(event.occurredAt, locale),
    actorName: event.actorKind === 'public' ? t('appointments.timeline.actorPublic') : event.actorKind === 'system' ? t('appointments.timeline.actorSystem') : t('appointments.timeline.actorStaff'),
    remarks: event.reason ?? undefined,
  }));

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[
          { label: t('appointments.inbox.title'), href: ROUTES.appointments },
          { label: appointment.referenceNo },
        ]}
        renderLink={(item) => (
          <Link to={item.href as string} className="text-text-link no-underline hover:underline">
            {item.label}
          </Link>
        )}
      />

      <PageHeader
        title={appointment.referenceNo}
        description={<StatusBadge entry={appointmentStatusEntry(appointment.status)} />}
        actions={
          canDecideFurther ? (
            <div className="flex flex-wrap gap-2">
              {appointment.status.code === 'pending' ? (
                <Button size="sm" onClick={() => setActiveModal('accept')}>
                  {t('appointments.action.accept')}
                </Button>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setActiveModal('reschedule')}>
                  {t('appointments.action.reschedule')}
                </Button>
              )}
              {appointment.status.code === 'pending' ? (
                <Button size="sm" variant="destructive" onClick={() => setActiveModal('decline')}>
                  {t('appointments.action.decline')}
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setActiveModal('cancel')}>
                  {t('appointments.action.cancel')}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      <Card className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
        <div>
          <div className="text-xs font-semibold uppercase text-text-muted">{t('appointments.detail.visitor')}</div>
          <div className="text-text-primary">{appointment.visitor.name}</div>
          <div className="text-sm text-text-muted">{appointment.visitor.phone}</div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase text-text-muted">{t('appointments.detail.when')}</div>
          <div className="text-text-primary">{formatInstantDisplay(appointment.scheduledStart ?? appointment.requestedStart, locale)}</div>
          {appointment.scheduledStart === null ? (
            <div className="text-sm text-text-muted">{t('appointments.detail.requestedOnly')}</div>
          ) : null}
        </div>
        <div className="sm:col-span-2">
          <div className="text-xs font-semibold uppercase text-text-muted">{t('appointments.detail.purpose')}</div>
          <div className="text-text-primary">{appointment.purpose}</div>
        </div>
        {appointment.targetAvailability ? (
          <div>
            <div className="text-xs font-semibold uppercase text-text-muted">{t('appointments.detail.myAvailability')}</div>
            <StatusBadge
              entry={availabilityStatusEntry(
                appointment.targetAvailability.statusCode,
                appointment.targetAvailability.labelNe,
                appointment.targetAvailability.labelEn,
              )}
            />
          </div>
        ) : null}
        {appointment.decline ? (
          <div className="sm:col-span-2">
            <div className="text-xs font-semibold uppercase text-text-muted">{t('appointments.detail.declineReason')}</div>
            <div className="text-text-primary">{appointment.decline.reason}</div>
          </div>
        ) : null}
      </Card>

      <div>
        <h2 className="mb-2 text-lg font-semibold text-text-heading">{t('appointments.detail.timeline')}</h2>
        <Timeline events={events} projection="internal" />
      </div>

      {activeModal === 'accept' || activeModal === 'reschedule' ? (
        <AppointmentScheduleModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          appointment={appointment}
          mode={activeModal}
          onDone={() => {
            invalidate();
            notifySuccess(activeModal === 'accept' ? 'toast.appointments.accepted' : 'toast.appointments.rescheduled');
          }}
          onStaleConflict={invalidate}
        />
      ) : null}
      {activeModal === 'decline' ? (
        <AppointmentDeclineModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          appointment={appointment}
          reasons={reasons}
          onDone={() => {
            invalidate();
            notifySuccess('toast.appointments.declined');
          }}
          onStaleConflict={invalidate}
        />
      ) : null}
      {activeModal === 'cancel' ? (
        <AppointmentCancelModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          appointment={appointment}
          onDone={() => {
            invalidate();
            notifySuccess('toast.appointments.cancelled');
          }}
          onStaleConflict={invalidate}
        />
      ) : null}
    </div>
  );
}
